import { SeverityIcon } from '@/ui/components/svg/SeverityIcon'
import React from 'react'
import { FilterModuleConfig, transformToGenericFilterConfig } from '../filters'
import { sortStrategies } from '@/ui/components/cases-table/CasesTable.utils'
import { UnifiedFilterConfig, SearchKeyConfig } from '@core/modules/filters/unified-filter.module'
import { TMdrSearchableTabs } from './mdr'

export type TMDRFilterType = 'severity' | 'status'

export const mdrSearchKeys = {
    incidents: [
        { value: 'title', threshold: 0.3 },
        { value: 'status', threshold: 0.2 },
        { value: 'severity', threshold: 0.2 },
        { value: 'caseNumber', threshold: 0.1 },
        { value: 'createdAt', threshold: 0.4 },
    ] as SearchKeyConfig[],
    alerts: [
        { value: 'title', threshold: 0.3 },
        { value: 'status', threshold: 0.2 },
        { value: 'severity', threshold: 0.2 },
        { value: 'caseNumber', threshold: 0.1 },
        { value: 'createdAt', threshold: 0.4 },
    ] as SearchKeyConfig[],
    serviceRequests: [
        { value: 'title', threshold: 0.3 },
        { value: 'status', threshold: 0.2 },
        { value: 'caseNumber', threshold: 0.1 },
        { value: 'createdAt', threshold: 0.4 },
    ] as SearchKeyConfig[],
}

const config = {
    incidents: {
        severity: {
            accessor: 'severity',
            label: 'Severity',
            collapsible: true,
            filters: {
                critical: {
                    accessor: 'critical',
                    label: 'Critical',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'critical',
                    }),
                },
                high: {
                    accessor: 'high',
                    label: 'High',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'high',
                    }),
                },
                medium: {
                    accessor: 'medium',
                    label: 'Medium',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'medium',
                    }),
                },
                low: {
                    accessor: 'low',
                    label: 'Low',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'low',
                    }),
                },
                informational: {
                    accessor: 'informational',
                    label: 'Informational',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'informational',
                    }),
                },
            },
        },
        status: {
            accessor: 'status',
            label: 'Status',
            collapsible: true,
            filters: {
                inProgress: {
                    accessor: 'inProgress',
                    label: 'In Progress',
                },
                waiting: {
                    accessor: 'waiting',
                    label: 'Waiting for Response',
                },
                closed: {
                    accessor: 'closed',
                    label: 'Closed',
                },
            },
        },
    },
    alerts: {
        severity: {
            accessor: 'severity',
            label: 'Severity',
            collapsible: true,
            filters: {
                critical: {
                    accessor: 'critical',
                    label: 'Critical',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'critical',
                    }),
                },
                high: {
                    accessor: 'high',
                    label: 'High',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'high',
                    }),
                },
                medium: {
                    accessor: 'medium',
                    label: 'Medium',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'medium',
                    }),
                },
                low: {
                    accessor: 'low',
                    label: 'Low',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'low',
                    }),
                },
                informational: {
                    accessor: 'informational',
                    label: 'Informational',
                    icon: React.createElement(SeverityIcon, {
                        severity: 'informational',
                    }),
                },
            },
        },
        status: {
            accessor: 'status',
            label: 'Status',
            collapsible: true,
            filters: {
                inProgress: {
                    accessor: 'inProgress',
                    label: 'In Progress',
                },
                closed: {
                    accessor: 'closed',
                    label: 'Closed',
                },
            },
        },
    },
    serviceRequests: {
        status: {
            accessor: 'status',
            label: 'Status',
            collapsible: true,
            filters: {
                open: {
                    accessor: 'open',
                    label: 'Open',
                },
                inProgress: {
                    accessor: 'inProgress',
                    label: 'In Progress',
                },
                waiting: {
                    accessor: 'waiting',
                    label: 'Waiting',
                },
                closed: {
                    accessor: 'closed',
                    label: 'Closed',
                },
            },
        },
    },
}

const defaultFilters = {
    incidents: {
        severity: {
            critical: false,
            high: false,
            medium: false,
            low: false,
            informational: false,
        },
        status: {
            inProgress: false,
            waiting: false,
            closed: false,
        },
    },
    alerts: {
        severity: {
            critical: false,
            high: false,
            medium: false,
            low: false,
            informational: false,
        },
        status: {
            inProgress: false,
            closed: false,
        },
    },
    serviceRequests: {
        status: {
            open: false,
            inProgress: false,
            waiting: false,
            closed: false,
        },
    },
}

export const mdrFilterConfigs: Record<string, FilterModuleConfig> =
    transformToGenericFilterConfig(config, defaultFilters)

export const createUnifiedMdrConfig = (
    tab: TMdrSearchableTabs
): UnifiedFilterConfig => ({
    ...mdrFilterConfigs[tab],
    searchKeys: mdrSearchKeys[tab],
    sortStrategy: (a: any, b: any) => {
        const sortFn = sortStrategies[tab]
        return sortFn ? sortFn(a, b) : 0
    },
})

export { config, defaultFilters }
